import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ClientPrivacyControls from "@/components/ClientPrivacyControls";
import { CONFIGURABLE_PAGES, ALL_SLUGS } from "@/lib/page-permissions";
import { privacyLabels } from "@/lib/client-privacy";
import { ShieldCheck, Plus, X, Send } from "lucide-react";

const INVITE_STATUS_STYLES = {
  sent:             { bg: "#FEF9C3", color: "#854D0E", label: "Invite sent" },
  delivered:        { bg: "#D1FAE5", color: "#065F46", label: "Delivered" },
  opened:           { bg: "#D1FAE5", color: "#065F46", label: "Opened" },
  clicked:          { bg: "#D1FAE5", color: "#065F46", label: "Clicked" },
  delivery_delayed: { bg: "#FEF9C3", color: "#854D0E", label: "Delayed" },
  bounced:          { bg: "#FEE2E2", color: "#991B1B", label: "Bounced" },
  complained:       { bg: "#FEE2E2", color: "#991B1B", label: "Complained" },
};

function InviteStatusBadge({ user }) {
  if (user.active) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
            style={{ background: "#D1FAE5", color: "#065F46" }}>
        Active
      </span>
    );
  }
  if (user.invite_email_status) {
    const s = INVITE_STATUS_STYLES[user.invite_email_status];
    if (s) {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
              style={{ background: s.bg, color: s.color }}>
          {s.label}
        </span>
      );
    }
  }
  if (user.invite_resend_id) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
            style={{ background: "#FEF9C3", color: "#854D0E" }}>
        Invite sent
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
          style={{ background: "var(--fb-border)", color: "var(--fb-text-muted)" }}>
      Invite pending
    </span>
  );
}

function RoleBadge({ role }) {
  if (role === "admin") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
            style={{ background: "rgba(5,47,102,0.1)", color: "var(--fb-primary)" }}>
        <ShieldCheck className="w-3 h-3" /> Admin
      </span>
    );
  }
  if (role === "viewer") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
            style={{ background: "#F0FDF4", color: "#166534" }}>
        Viewer
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
          style={{ background: "var(--fb-bg)", color: "var(--fb-text-muted)" }}>
      Staff
    </span>
  );
}

function PermissionsSummary({ user, pagePerms }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const isAdmin = user.role === "admin";

  if (isAdmin) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
            style={{ background: "rgba(5,47,102,0.1)", color: "var(--fb-primary)" }}>
        All pages
      </span>
    );
  }

  const effectivePerms = pagePerms !== null && pagePerms !== undefined ? pagePerms : ALL_SLUGS;
  const hasAll = ALL_SLUGS.every(s => effectivePerms.includes(s));

  if (hasAll) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
            style={{ background: "var(--fb-bg)", color: "var(--fb-text-muted)" }}>
        All pages
      </span>
    );
  }

  const labels = CONFIGURABLE_PAGES
    .filter(p => effectivePerms.includes(p.slug))
    .map(p => p.label);

  if (labels.length === 0) {
    return <span className="text-xs" style={{ color: "var(--fb-text-muted)" }}>None</span>;
  }

  return (
    <div className="relative inline-block">
      <span
        className="inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full cursor-default select-none"
        style={{ background: "var(--fb-bg)", color: "var(--fb-text-muted)", border: "1px solid var(--fb-border)" }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {labels.length} {labels.length === 1 ? "page" : "pages"}
      </span>
      {showTooltip && (
        <div
          className="absolute z-50 bottom-full left-0 mb-1.5 rounded-lg shadow-lg text-xs py-2 px-3 whitespace-nowrap"
          style={{ background: "var(--fb-card)", border: "1px solid var(--fb-border)", color: "var(--fb-text)" }}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <div className="font-semibold mb-1" style={{ color: "var(--fb-text-muted)" }}>Page access</div>
          <ul className="space-y-0.5">
            {labels.map(label => (
              <li key={label}>{label}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function StaffUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [showNew, setShowNew] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/staff");
      setUsers(data);
    } catch (e) { setError(formatApiError(e)); }
  }
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="staff-users-page">
      <div className="flex items-start sm:items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-2" style={{ color: "#8B918B" }}>Administration</div>
          <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "Outfit" }}>Staff Logins</h1>
          <p className="text-sm mt-2" style={{ color: "var(--fb-text-muted)" }}>
            Create, edit, and remove staff accounts. Click any row to edit. Admins can manage other staff; staff have full client-facing access only.
          </p>
        </div>
        <button data-testid="new-staff-btn" onClick={() => setShowNew(true)}
                className="fb-btn-primary px-4 sm:px-5 py-3 rounded-xl font-semibold inline-flex items-center gap-2 text-sm sm:text-base">
          <Plus className="w-4 h-4" /> New Staff
        </button>
      </div>

      {error && <div className="fb-status-error text-sm p-3 rounded-lg">{error}</div>}

      <div className="fb-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[580px]">
            <thead>
              <tr style={{ background: "var(--fb-bg)" }}>
                <th className="text-left text-xs uppercase tracking-wider font-semibold px-6 py-4" style={{ color: "var(--fb-text-muted)" }}>Email</th>
                <th className="text-left text-xs uppercase tracking-wider font-semibold px-6 py-4" style={{ color: "var(--fb-text-muted)" }}>Name</th>
                <th className="text-left text-xs uppercase tracking-wider font-semibold px-6 py-4" style={{ color: "var(--fb-text-muted)" }}>Role</th>
                <th className="text-left text-xs uppercase tracking-wider font-semibold px-6 py-4" style={{ color: "var(--fb-text-muted)" }}>Status</th>
                <th className="text-left text-xs uppercase tracking-wider font-semibold px-6 py-4" style={{ color: "var(--fb-text-muted)" }}>Created</th>
                <th className="text-left text-xs uppercase tracking-wider font-semibold px-6 py-4" style={{ color: "var(--fb-text-muted)" }}>Permissions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <StaffRow key={u.id} user={u} isSelf={me?.id === u.id} />
              ))}
              {users.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-12 text-center text-sm" style={{ color: "var(--fb-text-muted)" }}>
                  No staff accounts yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showNew && <NewStaffModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function StaffRow({ user, isSelf }) {
  const navigate = useNavigate();

  return (
    <tr
      className="border-t cursor-pointer transition-colors"
      style={{ borderColor: "var(--fb-border)" }}
      onClick={() => navigate(`/staff/users/${user.id}`)}
      onMouseEnter={e => e.currentTarget.style.background = "var(--fb-bg)"}
      onMouseLeave={e => e.currentTarget.style.background = ""}
      data-testid={`staff-row-${user.id}`}
    >
      <td className="px-6 py-4 max-w-[200px]">
        <div className="font-semibold flex items-center gap-2 min-w-0" style={{ color: "var(--fb-text)" }}>
          <span className="truncate">{user.email}</span>
          {isSelf && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                  style={{ background: "var(--fb-bg)", color: "var(--fb-text-muted)", border: "1px solid var(--fb-border)" }}>
              you
            </span>
          )}
        </div>
      </td>
      <td className="px-6 py-4 text-sm" style={{ color: "var(--fb-text)" }}>
        {user.name || <span style={{ color: "var(--fb-text-muted)" }}>—</span>}
      </td>
      <td className="px-6 py-4">
        <RoleBadge role={user.role} />
      </td>
      <td className="px-6 py-4">
        <InviteStatusBadge user={user} />
      </td>
      <td className="px-6 py-4 text-xs tabular-nums" style={{ color: "var(--fb-text-muted)" }}>
        {user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}
      </td>
      <td className="px-6 py-4">
        <PermissionsSummary user={user} pagePerms={user.page_permissions} />
        {privacyLabels(user).length > 0 && (
          <div className="text-xs mt-1" style={{ color: "var(--fb-text-muted)" }}>
            Hidden: {privacyLabels(user).join(", ")}
          </div>
        )}
      </td>
    </tr>
  );
}

function NewStaffModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ email: "", role: "staff", hidden_client_fields: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function save(e) {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      const { data } = await api.post("/staff", form);
      await api.post(`/staff/${data.id}/invite`);
      onCreated();
    } catch (e) { setError(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-black/40 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md my-8 p-6 sm:p-7" onClick={(e) => e.stopPropagation()} data-testid="new-staff-modal">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>Invite Staff Member</h3>
            <p className="text-sm mt-1" style={{ color: "var(--fb-text-muted)" }}>
              An email with a setup link will be sent to the address below.
            </p>
          </div>
          <button onClick={onClose} aria-label="Close"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={save} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1.5">Email</label>
            <input data-testid="new-staff-email" type="email" required value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })}
                   className="w-full px-3.5 py-2.5 rounded-lg border outline-none focus:border-[#052F66]"
                   style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Role</label>
            <select data-testid="new-staff-role" value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="w-full px-3.5 py-2.5 rounded-lg border bg-white" style={{ borderColor: "var(--fb-border)" }}>
              <option value="viewer">Viewer — read-only: Dashboard, Clients, Pending, Reports</option>
              <option value="staff">Staff — read/write access</option>
              <option value="admin">Admin — full access + can manage other staff</option>
            </select>
          </div>

          <ClientPrivacyControls value={form.hidden_client_fields}
            onChange={(hidden_client_fields) => setForm({ ...form, hidden_client_fields })} />

          {error && <div className="fb-status-error text-sm p-3 rounded-lg" data-testid="new-staff-error">{error}</div>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-lg border" style={{ borderColor: "var(--fb-border)" }}>Cancel</button>
            <button data-testid="create-staff-submit" type="submit" disabled={saving}
                    className="fb-btn-primary px-5 py-2.5 rounded-lg font-semibold inline-flex items-center gap-2">
              <Send className="w-4 h-4" />
              {saving ? "Sending invite…" : "Send Invite"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
